import { InvitedParticipant, InviteEmailStatus, OptionsApi } from '@core/swagger-client';
import {
    Button,
    Check,
    Clock,
    Close,
    CompositePageHeader,
    ContainerNoTopSpace,
    Dialog,
    ErrorMessagePage,
    Heading,
    Info,
    Input,
    Loader,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    Spinner,
    Switch,
    Warning,
    WebinarNotPublishedWarning
} from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink, Link } from '@online-webinar/ui-frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { getI18n, Trans, useTranslation } from 'react-i18next';
import { ContactBody, ContactCard } from '../../molecules';

const ChunkSize = 25;

const InviteStatusIcon = ({ status, errorMessage }: { status: InviteEmailStatus; errorMessage?: string }) => {
    const label = InviteEmailStatus[status] + (errorMessage ? `: ${errorMessage}` : '');
    switch (status) {
        case InviteEmailStatus.NotSent:
            return (
                <span title={label}>
                    <Clock classNames="size-5 stroke-gray-400" />
                </span>
            );
        case InviteEmailStatus.Queued:
            return (
                <span title={label}>
                    <Spinner classNames="text-primary" />
                </span>
            );
        case InviteEmailStatus.Sent:
            return (
                <span title={label}>
                    <Check classNames="size-5 stroke-success" />
                </span>
            );
        case InviteEmailStatus.Error:
            return (
                <span title={label}>
                    <Warning classNames="size-5" />
                </span>
            );
        case InviteEmailStatus.Skipped:
            return (
                <span title={label}>
                    <Info classNames="size-5 stroke-gray-400" />
                </span>
            );
    }
};

export const InvitedParticipants = () => {
    const { webinarId } = useParams({ strict: false });
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const {
        data: invites,
        isLoading,
        error: invitesError,
        refetch: refreshInvites
    } = useQuery({
        ...OptionsApi.invites.get(webinarId!),
        refetchInterval: (query) => {
            const data = query.state.data as InvitedParticipant[] | undefined;
            return data?.some((i) => i.inviteEmailStatus === InviteEmailStatus.Queued || i.reminderEmailStatus === InviteEmailStatus.Queued) ? 3000 : false;
        }
    });
    const { data: webinar, isLoading: isWebinarLoading, error: webinarError } = useQuery(OptionsApi.dashboard.getContentSummary(webinarId!));
    const { data: settings } = useQuery(OptionsApi.invites.getSettings(webinarId!));

    const updateSettingsMutation = useMutation(
        OptionsApi.invites.updateSettings({
            onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invites', 'settings', webinarId] })
        })
    );

    const updateSettings = (patch: Partial<{ autoSendInvite: boolean; autoSendReminder: boolean; reminderHoursBeforeWebinar: number }>) => {
        if (!settings) return;
        updateSettingsMutation.mutate({ webinarId: webinarId!, settings: { ...settings, ...patch } });
    };

    const [chunkIndex, setChunkIndex] = useState<number>(-1);
    const [chunks, setChunks] = useState<string[][]>([]);
    const [emailing, setEmailing] = useState<boolean>(false);
    const [emailingReminder, setEmailingReminder] = useState<boolean>(false);

    const emailMutation = useMutation(OptionsApi.invites.email());
    const emailReminderMutation = useMutation(OptionsApi.invites.emailReminder());

    const emailParticipants = (notSentOnly: boolean) => {
        if (!invites || invites.length === 0) {
            return;
        }

        const toInvite = notSentOnly ? invites.filter((invite) => invite.inviteEmailStatus === InviteEmailStatus.NotSent) : invites;
        if (toInvite.length === 0) {
            return;
        }

        const emailAddresses = toInvite.map((invite) => invite.emailAddress);
        if (emailAddresses.length < ChunkSize) {
            emailMutation.mutate(
                { webinarId: webinarId!, emailAddresses: toInvite.map((invite) => invite.emailAddress) },
                {
                    onSuccess: () => {
                        refreshInvites();
                    }
                }
            );
        } else {
            const chunks: string[][] = [];
            while (emailAddresses.length > 0) {
                chunks.push(emailAddresses.splice(0, ChunkSize));
            }
            setChunks(chunks);
            setChunkIndex(0);
            setEmailing(true);
        }
    };

    const emailReminderParticipants = (notSentOnly: boolean) => {
        if (!invites || invites.length === 0) {
            return;
        }

        const toRemind = notSentOnly ? invites.filter((invite) => invite.reminderEmailStatus === InviteEmailStatus.NotSent) : invites;
        if (toRemind.length === 0) {
            return;
        }

        const emailAddresses = toRemind.map((invite) => invite.emailAddress);
        if (emailAddresses.length < ChunkSize) {
            emailReminderMutation.mutate({ webinarId: webinarId!, emailAddresses }, { onSuccess: () => refreshInvites() });
        } else {
            const chunks: string[][] = [];
            while (emailAddresses.length > 0) {
                chunks.push(emailAddresses.splice(0, ChunkSize));
            }
            setChunks(chunks);
            setChunkIndex(0);
            setEmailingReminder(true);
        }
    };

    useEffect(() => {
        if (!emailing || chunkIndex < 0 || chunkIndex >= chunks.length) {
            setEmailing(false);
            return;
        }

        emailMutation.mutate(
            { webinarId: webinarId!, emailAddresses: chunks[chunkIndex] },
            {
                onError: () => {
                    setEmailing(false);
                    setChunks([]);
                    setChunkIndex(-1);
                    refreshInvites();
                },
                onSuccess: () => {
                    if (chunkIndex + 1 < chunks.length) {
                        setChunkIndex(chunkIndex + 1);
                    } else {
                        setEmailing(false);
                        setChunks([]);
                        setChunkIndex(-1);
                        refreshInvites();
                    }
                }
            }
        );
    }, [chunks, chunkIndex, emailing]);

    useEffect(() => {
        if (!emailingReminder || chunkIndex < 0 || chunkIndex >= chunks.length) {
            setEmailingReminder(false);
            return;
        }

        emailReminderMutation.mutate(
            { webinarId: webinarId!, emailAddresses: chunks[chunkIndex] },
            {
                onError: () => {
                    setEmailingReminder(false);
                    setChunks([]);
                    setChunkIndex(-1);
                    refreshInvites();
                },
                onSuccess: () => {
                    if (chunkIndex + 1 < chunks.length) {
                        setChunkIndex(chunkIndex + 1);
                    } else {
                        setEmailingReminder(false);
                        setChunks([]);
                        setChunkIndex(-1);
                        refreshInvites();
                    }
                }
            }
        );
    }, [chunks, chunkIndex, emailingReminder]);

    const deleteParticipantMutation = useMutation(OptionsApi.invites.remove());
    const deleteParticipant = (invite: InvitedParticipant) => {
        deleteParticipantMutation.mutate(
            { webinarId: webinarId!, emailAddress: invite.emailAddress },
            {
                onSuccess: () => {
                    refreshInvites();
                }
            }
        );
    };

    const error = invitesError ?? webinarError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isWebinarLoading || !webinar || isLoading || !invites) {
        return <LoaderPage />;
    }

    return (
        <ContainerNoTopSpace>
            <ContactCard>
                <PageHeader>
                    <PageHeaderContent>
                        <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />
                    </PageHeaderContent>
                </PageHeader>

                {!webinar.isPublished && <WebinarNotPublishedWarning />}

                <ContactBody>
                    <BackToOriginLink fallback="content" />
                    <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                        {t('Invites.PageHeading')}
                    </Heading>
                    <Link to="/$webinarId/invites/import" params={{ webinarId }} variant="primary" disabled={!webinar.isPublished}>
                        {t('Invites.Button.Import')}
                    </Link>

                    {settings && (
                        <div className="mt-6 flex flex-col gap-3">
                            <Switch
                                label={t('Invites.Settings.AutoSendInvite')}
                                checked={settings.autoSendInvite}
                                onChange={(e) => updateSettings({ autoSendInvite: e.target.checked })}
                            />
                            <Switch
                                label={t('Invites.Settings.AutoSendReminder')}
                                checked={settings.autoSendReminder}
                                onChange={(e) => updateSettings({ autoSendReminder: e.target.checked })}
                            />
                            {settings.autoSendReminder && (
                                <div className="max-w-lg mb-6 pl-10">
                                    <Input
                                        label={t('Invites.Settings.ReminderHoursBeforeWebinar')}
                                        type="number"
                                        min={0.5}
                                        step={0.5}
                                        value={settings.reminderHoursBeforeWebinar}
                                        onChange={(e) => updateSettings({ reminderHoursBeforeWebinar: parseFloat(e.target.value) })}
                                        classNames="w-24"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    <Dialog open={emailing || emailingReminder} title={t('Invites.Dialog.Emailing.Title')}>
                        <div className="flex flex-col gap-4 items-center">
                            <Loader />
                            {chunks.length > 0 && (
                                <p>
                                    {t('Invites.Dialog.Emailing.Progress', {
                                        current: chunkIndex * ChunkSize,
                                        total: (chunks.length - 1) * ChunkSize + chunks[chunks.length - 1].length
                                    })}
                                </p>
                            )}
                        </div>
                    </Dialog>

                    {invites.length > 0 && (
                        <>
                            <Heading variant="h2" classNames="mt-10 2xl:mt-12 mb-4 lg:mb-6 2xl:mb-8">
                                {t('Invites.Heading.ImportedParticipants')}
                            </Heading>
                            <div className="flex flex-wrap gap-4">
                                <Button
                                    onClick={() => emailParticipants(true)}
                                    isLoading={emailMutation.isPending || emailReminderMutation.isPending || emailing || emailingReminder}
                                    variant="primary"
                                    disabled={!webinar.isPublished}
                                >
                                    {t('Invites.Button.EmailPending')}
                                </Button>
                                <Button
                                    onClick={() => emailParticipants(false)}
                                    isLoading={emailMutation.isPending || emailReminderMutation.isPending || emailing || emailingReminder}
                                    variant="primary"
                                    disabled={!webinar.isPublished}
                                >
                                    {t('Invites.Button.ResendAllInvites')}
                                </Button>
                                <Button
                                    onClick={() => emailReminderParticipants(true)}
                                    isLoading={emailReminderMutation.isPending || emailMutation.isPending || emailing || emailingReminder}
                                    variant="primary"
                                    disabled={!webinar.isPublished}
                                >
                                    {t('Invites.Button.EmailReminder')}
                                </Button>
                                <Button
                                    onClick={() => emailReminderParticipants(false)}
                                    isLoading={emailReminderMutation.isPending || emailMutation.isPending || emailing || emailingReminder}
                                    variant="primary"
                                    disabled={!webinar.isPublished}
                                >
                                    {t('Invites.Button.ResendAllReminders')}
                                </Button>
                            </div>

                            <div className="table-responsive bg-white rounded-lg mt-4 shadow-md">
                                <table className="table table-striped mt-2">
                                    <thead>
                                        <tr>
                                            <th>{t('Account.Label.Email')}</th>
                                            <th>{t('Account.Label.Nickname')}</th>
                                            <th>{t('Account.Label.FirstName')}</th>
                                            <th>{t('Account.Label.LastName')}</th>
                                            <th>{t('Account.Label.CompanyName')}</th>
                                            <th>{t('Invites.Table.Header.InvitedAt')}</th>
                                            <th>{t('Invites.Table.Header.Status')}</th>
                                            <th>{t('Invites.Table.Header.ReminderStatus')}</th>
                                            <th className="w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invites.map((invite, rowIndex) => (
                                            <tr key={rowIndex}>
                                                <td>{invite.emailAddress}</td>
                                                <td>{invite.nickname}</td>
                                                <td>{invite.firstName}</td>
                                                <td>{invite.lastName}</td>
                                                <td>{invite.companyName}</td>
                                                <td>
                                                    {DateTime.fromISO(invite.invitedAt)
                                                        .toLocal()
                                                        .setLocale(getI18n().language)
                                                        .toLocaleString(DateTime.DATETIME_SHORT)}
                                                </td>
                                                <td>
                                                    <InviteStatusIcon status={invite.inviteEmailStatus} errorMessage={invite.inviteErrorMessage} />
                                                </td>
                                                <td>
                                                    <InviteStatusIcon status={invite.reminderEmailStatus} errorMessage={invite.reminderErrorMessage} />
                                                </td>
                                                <td>
                                                    <Button
                                                        onClick={() => deleteParticipant(invite)}
                                                        iconbtn
                                                        classNames="-my-4 translate-y-1.25"
                                                        isLoading={deleteParticipantMutation.isPending}
                                                    >
                                                        <Close classNames="size-6 stroke-warning" />
                                                        <span className="sr-only">
                                                            <Trans values={{ email: invite.emailAddress }} i18nKey="Invites.Button.DeleteParticipant" />
                                                        </span>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </ContactBody>
            </ContactCard>
        </ContainerNoTopSpace>
    );
};
